// filepath: apps/web/src/operator/SummaryCard.tsx
//
// FR-UI-OPS-1 AC-3 (a/c/e) / FR-UI-OPS-3 — three headline metrics +
// the `computed_at` timestamp.
//
// Renders:
//   - tenant count     = `tenants.length`
//   - active instances = `totals.instanceCount`
//   - error rate       = `Math.round((totals.errorCount / totals.instanceCount) * 100)%`
//                       (with "0%" fallback when instanceCount === 0;
//                        never "NaN%").
//
// The timestamp is rendered in the user's locale via the
// shared `formatDate` helper (FR-I18N-2). The locale comes from
// the i18n bundle (the same locale FR-UI-CORE-9 writes to
// <html lang>).
//
// INVARIANTS:
//   - All chrome goes through `useTranslation().t()`. No hardcoded
//     English / Russian / Kazakh strings.
//   - No `dangerouslySetInnerHTML` (INV-9).
//   - The error rate display is computed via the spec
//     (AC-3 / TestDesigner); tests cover the "0%" and "NaN%"
//     branches.
//
// FR-UI-OPS-3 redesign: each metric now renders inside its own
// `Card` primitive (3-column grid per the design composition). The
// per-metric test ids are preserved verbatim.
//
// A11y follow-up (ISS-288, ISS-289 — wf-f4fa1b7b-20260819-req-656
// cascading fix): the metrics grid uses `<h3>` + `<p>` (was
// `<dt>`/`<dd>`) to avoid axe-core's `dlitem` rule, which only
// allows one level of `<div>` between `<dl>` and `<dt>` — but
// `<Card>` adds an extra `<div>` wrapper that breaks the rule
// (would be `<dl> > <div> > <div> > <dt>`). Switching to `<h3>`/`<p>`
// eliminates the rule while preserving the visible chrome and the
// `data-testid` hooks. The outer container is a plain `<div>`
// (was a `<section aria-labelledby>`) so it does not collide with
// the page-level `<section>` in `OperatorSummaryRoute.tsx`, which
// already uses the same `operator.summary.title` i18n key
// (axe-core `landmark-unique`, moderate).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { Locale } from "@/i18n/useTranslation";
import { formatDate, formatNumber } from "@/i18n/format";
import { Card } from "@/design-system";
import type { Summary } from "./useOperatorSummary";

export interface SummaryCardProps {
  summary: Summary;
}

/**
 * Compute the error rate string. Pure: returns "0%" when
 * instanceCount === 0 (never "NaN%"). Otherwise
 * `Math.round((errorCount / instanceCount) * 100) + "%"`.
 */
export function formatErrorRate(
  errorCount: number,
  instanceCount: number,
): string {
  if (!Number.isFinite(instanceCount) || instanceCount === 0) return "0%";
  if (!Number.isFinite(errorCount)) return "0%";
  const pct = Math.round((errorCount / instanceCount) * 100);
  if (!Number.isFinite(pct)) return "0%";
  return `${pct}%`;
}

/**
 * Format the computed-at timestamp in the user's locale. Thin
 * wrapper over `formatDate` from `@/i18n/format` (FR-I18N-2):
 * preserves the existing public contract (an invalid Date returns
 * `"—"`) so the white-box tests in `summaryCard.test.tsx` continue
 * to pass unchanged. All rendering delegates to the shared helper.
 */
export function formatComputedAt(computedAt: Date, locale: Locale): string {
  return formatDate(computedAt, locale);
}

/**
 * SummaryCard — three headline metrics + computed-at timestamp.
 * Pure presentation; takes a `Summary` and renders it.
 *
 * FR-UI-OPS-3 chrome: each metric lives in its own design-system
 * `Card` with `padding="md"`. The 3-column grid (CSS grid) mirrors
 * the design composition `docs/design/FR-UI-OPS-3/composition/summary/ready.html`.
 * The metric-level test ids (`operator-summary-card-tenant-count`,
 * `operator-summary-card-instance-count`, `operator-summary-card-error-rate`,
 * `operator-summary-card-computed-at`) are preserved so
 * `summaryCard.test.tsx` continues to pass unchanged.
 */
export function SummaryCard({ summary }: SummaryCardProps): React.ReactElement {
  const { t, locale } = useTranslation();
  const tenantCount = summary.tenants.length;
  const instanceCount = summary.totals.instanceCount;
  const errorRate = formatErrorRate(
    summary.totals.errorCount,
    summary.totals.instanceCount,
  );
  const computedAt = formatComputedAt(summary.computedAt, locale);

  return (
    <div
      data-testid="operator-summary-card"
      className="operator-summary-card"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-4)",
      }}
    >
      <h2
        id="operator-summary-card-title"
        className="operator-summary-card-title"
        style={{
          fontSize: "var(--text-2xl)",
          fontWeight: "var(--font-extrabold)",
          color: "var(--text-primary)",
          margin: 0,
        }}
      >
        {t("operator.summary.title", "Cross-tenant summary")}
      </h2>
      <div
        className="operator-summary-card-metrics"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "var(--space-4)",
          margin: 0,
          padding: 0,
        }}
      >
        <Card padding="md">
          <div
            data-testid="operator-summary-card-tenant-count"
            className="operator-summary-card-metric"
          >
            <h3
              style={{
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-medium)",
                color: "var(--text-secondary)",
                margin: 0,
                marginBottom: "var(--space-2)",
              }}
            >
              {t("operator.summary.tenantCount", "Tenants")}
            </h3>
            <p
              style={{
                fontSize: "var(--text-3xl)",
                fontWeight: "var(--font-bold)",
                color: "var(--text-primary)",
                margin: 0,
              }}
            >
              {formatNumber(tenantCount, locale)}
            </p>
          </div>
        </Card>
        <Card padding="md">
          <div
            data-testid="operator-summary-card-instance-count"
            className="operator-summary-card-metric"
          >
            <h3
              style={{
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-medium)",
                color: "var(--text-secondary)",
                margin: 0,
                marginBottom: "var(--space-2)",
              }}
            >
              {t("operator.summary.activeInstances", "Active instances")}
            </h3>
            <p
              style={{
                fontSize: "var(--text-3xl)",
                fontWeight: "var(--font-bold)",
                color: "var(--text-primary)",
                margin: 0,
              }}
            >
              {formatNumber(instanceCount, locale)}
            </p>
          </div>
        </Card>
        <Card padding="md">
          <div
            data-testid="operator-summary-card-error-rate"
            className="operator-summary-card-metric"
          >
            <h3
              style={{
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-medium)",
                color: "var(--text-secondary)",
                margin: 0,
                marginBottom: "var(--space-2)",
              }}
            >
              {t("operator.summary.errorRate", "Error rate")}
            </h3>
            <p
              style={{
                fontSize: "var(--text-3xl)",
                fontWeight: "var(--font-bold)",
                color: "var(--color-danger-hover)",
                margin: 0,
              }}
            >
              {errorRate}
            </p>
          </div>
        </Card>
      </div>
      <p
        data-testid="operator-summary-card-computed-at"
        className="operator-summary-card-computed-at"
        style={{
          fontSize: "var(--text-sm)",
          color: "var(--text-secondary)",
          margin: 0,
        }}
      >
        <span className="visually-hidden">
          {t("operator.summary.computedAt", "Last updated")}
          {": "}
        </span>
        <span aria-hidden="true">{computedAt}</span>
      </p>
    </div>
  );
}
